import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Clock, PiggyBank, TrendingUp } from "lucide-react";
import Reveal from "@/components/ui/reveal";

const MIN_PER_INVOICE = 9;     // minut ręcznej obróbki 1 dokumentu
const HOURLY_VALUE = 90;       // zł — wartość Twojej godziny
const PRO_PRICE = 34.99;       // zł / mc

const zl = (n) =>
    new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 }).format(n);

export default function RoiCalculator() {
    const [invoices, setInvoices] = useState(40);

    const { hours, saved, yearly } = useMemo(() => {
        const minutesSaved = invoices * MIN_PER_INVOICE * 0.8; // 80% czasu przejmuje automatyzacja
        const hours = minutesSaved / 60;
        const gross = hours * HOURLY_VALUE;
        const saved = Math.max(0, gross - PRO_PRICE);
        return { hours, saved, yearly: saved * 12 };
    }, [invoices]);

    return (
        <Reveal className="rounded-3xl border border-border bg-card p-8 sm:p-10">
            <div data-testid="roi-calculator">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-gr-accent">Kalkulator ROI</p>
                <h3 className="mt-3 text-2xl tracking-tight text-foreground sm:text-3xl">Ile odzyskasz z Groszownikiem?</h3>
                <p className="mt-3 text-sm text-muted-foreground">Przesuń suwak i zobacz, ile czasu i pieniędzy oszczędzasz miesięcznie na automatyzacji księgowości.</p>

                <div className="mt-8">
                    <div className="flex items-end justify-between">
                        <label className="text-sm font-medium text-foreground">Faktury i dokumenty miesięcznie</label>
                        <span className="font-mono text-2xl font-bold text-gr-primary dark:text-gr-dprimary" data-testid="roi-invoices-value">{invoices}</span>
                    </div>
                    <Slider
                        value={[invoices]}
                        min={1}
                        max={200}
                        step={1}
                        onValueChange={(v) => setInvoices(v[0])}
                        data-testid="roi-slider"
                        className="mt-4"
                        aria-label="Liczba faktur miesięcznie"
                    />
                    <div className="mt-2 flex justify-between font-mono text-[11px] text-muted-foreground">
                        <span>1</span>
                        <span>200</span>
                    </div>
                </div>

                <div className="mt-8 grid gap-4 sm:grid-cols-3">
                    <div className="rounded-2xl border border-border bg-gr-surface2 p-5 dark:bg-gr-dsurface2">
                        <Clock className="h-5 w-5 text-gr-accent" />
                        <p className="mt-3 font-mono text-2xl font-bold text-foreground" data-testid="roi-hours">{hours.toFixed(1)} h</p>
                        <p className="text-xs text-muted-foreground">zaoszczędzonego czasu / mc</p>
                    </div>
                    <div className="rounded-2xl border border-border bg-gr-surface2 p-5 dark:bg-gr-dsurface2">
                        <PiggyBank className="h-5 w-5 text-gr-accent" />
                        <p className="mt-3 font-mono text-2xl font-bold text-foreground" data-testid="roi-monthly">{zl(saved)}</p>
                        <p className="text-xs text-muted-foreground">oszczędności / mc (po PRO)</p>
                    </div>
                    <div className="rounded-2xl border border-gr-primary bg-gr-primary p-5 text-white dark:border-gr-dprimary">
                        <TrendingUp className="h-5 w-5 text-gr-accent" />
                        <p className="mt-3 font-mono text-2xl font-bold" data-testid="roi-yearly">{zl(yearly)}</p>
                        <p className="text-xs text-white/70">oszczędności rocznie</p>
                    </div>
                </div>
                <p className="mt-4 text-[11px] text-muted-foreground">
                    Szacunek poglądowy: {MIN_PER_INVOICE} min/dokument, 80% czasu przejmuje automatyzacja, wartość godziny {zl(HOURLY_VALUE)}, minus PRO {zl(PRO_PRICE)}/mc.
                </p>
            </div>
        </Reveal>
    );
}
